// Static configuration shared by the BBB page and its sub-components.
// Extracted from BallByBallPage.jsx to keep the page file focused on layout.

export const BREAK_TYPES = [
  // Test-format meal/end-of-day breaks (in roughly chronological order).
  "Lunch (Test matches)",
  "Tea (Test matches)",
  "Dinner (D/N Test - after twilight)",
  "Stumps (Test - end of day)",
  // Cross-format intervals
  "Innings Break",
  "Drinks Break",
  "Strategic Timeout (T20)",
  // Stoppages
  "Rain / Weather Delay",
  "Bad Light Delay",
  "Player Injury / Medical Timeout",
  "DRS / Umpires Review Pause",
  "Equipment Change (Bat/Ball)",
  "Sight Screen / Crowd Delay",
  "Super Over Interval (if tied)",
];

export const ACTIVITIES_CONFIG = {
  "Delivery Type": {
    fieldName: "delivery_type",
    options: {
      "Pace Bowler": [
        "No Movement",
        "In Swinger",
        "Out Swinger",
        "Seam In",
        "Seam Away",
        "Reverse Inswinger",
        "Reverse Outswinger",
        "Angled In",
        "Angled Away",
        "Slower Ball",
        "Knuckle Ball",
        "Off Cutter",
        "Leg Cutter",
        "Back of Hand",
      ],
      Spinner: [
        "No Turn",
        "Off Break",
        "Left arm Orthodox",
        "Arm Ball",
        "Carrom Ball",
        "Doosra",
        "Slider",
        "Leg Spinner",
        "Googly",
        "Drifter",
        "Top Spinner",
        "Flipper",
        "Quicker Ball",
      ],
    },
  },
  "Shot Connection": {
    fieldName: "shot_connection",
    options: {
      Contact: [
        "Normal",
        "Well Timed",
        "Middled",
        "Mis-timed",
        "Play and Miss",
        "Play and Miss (Legside)",
        "Shoulder Arms",
        "Left",
        "Bat-pad",
        "Padded",
        "Gloved",
        "Hit Pad",
        "Hit Body",
        "Hit Helmet",
        "Outside Edge",
        "Inside Edge",
        "Top Edge",
        "Bottom Edge",
        "Leading Edge",
        "Thick Edge",
        "Spliced",
      ],
    },
  },
  "Foot Movement": {
    fieldName: "foot_movement",
    items: [
      "No Movement",
      "Front Foot",
      "Back Foot",
      "Down the Track",
      "Backs Away",
      "Ducked",
      "Swayed Away",
      "Deep in Crease",
      "Moves Infront",
      "Moves Offside",
      "Moves Legside",
    ],
  },
  "Air Movement": {
    fieldName: "air_movement",
    items: ["Ground", "Flat", "Loopy", "High"],
  },
  Control: {
    fieldName: "control",
    items: ["Air Controlled", "Un Contolled"],
  },
  Stroke: {
    fieldName: "stroke",
    options: {
      Defence: [
        "No shot",
        "Leave",
        "Forward Defence",
        "Backward Defence",
        "Padded",
        "Fended",
      ],
      Offence: [
        "Straight Drive",
        "Off Drive",
        "On Drive",
        "Cover Drive",
        "Square Drive",
        "Flick",
        "Slog",
        "Sweep",
        "Reverse Sweep",
        "Slog Sweep",
        "Paddle Sweep",
        "Scoop",
        "Reverse Scoop",
        "Paddle Scoop",
        "Switch Hit",
        "Cut",
        "Late Cut",
        "Upper Cut",
        "Square Cut",
        "Pull",
        "Hook",
        "Worked",
        "Pushed",
        "Steer",
        "Dropped",
        "Glance",
        "Guided"
      ],
    },
  },
};

// Tag delivery types so we can hide spin-only / pace-only chips automatically
export const PACE_DELIVERIES = new Set([
  "No Movement","In Swinger","Out Swinger","Seam In","Seam Away",
  "Reverse Inswinger","Reverse Outswinger","Angled In","Angled Away",
  "Slower Ball","Knuckle Ball","Off Cutter","Leg Cutter","Back of Hand",
]);
export const SPIN_DELIVERIES = new Set([
  "No Turn","Off Break","Left arm Orthodox","Arm Ball","Carrom Ball",
  "Doosra","Slider","Leg Spinner","Googly","Drifter","Top Spinner",
  "Flipper","Quicker Ball",
]);

export function bowlerTypeFromDelivery(deliveryType) {
  if (!deliveryType) return null;
  if (SPIN_DELIVERIES.has(deliveryType)) return "SPIN";
  if (PACE_DELIVERIES.has(deliveryType)) return "PACE";
  return null;
}

// Mark each group with its relevance — ALL = always show, PACE/SPIN = bowler-type-only.
ACTIVITIES_CONFIG["Delivery Type"].relevant   = "ALL";
ACTIVITIES_CONFIG["Foot Movement"].relevant   = "ALL";
ACTIVITIES_CONFIG["Air Movement"].relevant    = "ALL";
ACTIVITIES_CONFIG["Control"].relevant         = "ALL";
ACTIVITIES_CONFIG["Stroke"].relevant          = "ALL";
ACTIVITIES_CONFIG["Shot Connection"].relevant = "ALL";

// New chip groups (replacing the dropdown-based Modern Analytics panel)
// Stance removed     — duplicated Foot Movement (per Vikram, 2026-05-07).
// Shot Intent removed — overlapping/contradictory categories; Shot Connection
//                      covers beaten / inside-edge / outside-edge for false-shot %
//                      (per Vikram, 2026-05-07).
// Outcome + Contact removed (2026-05-08) — both fully subsumed by Shot Connection:
//   Outcome's MIDDLED/MISTIMED/EDGED/BEATEN/PLAY_AND_MISS/LEFT/PAD/MISSED → all
//     present in Shot Connection (Middled, Mis-timed, plus 6 edge types, plus
//     Play and Miss, Left, Padded, Missed-equivalents).
//   Contact's Inside-edge/Outside-edge/Top-edge/Bottom-edge/Pad/Glove/Missed →
//     also all in Shot Connection. The DB columns (outcome_quality, bat_subject)
//     remain on the model — historical balls keep their values; new balls just
//     leave them empty. Removing the chip groups, not the storage.
ACTIVITIES_CONFIG["Keeper"] = {
  fieldName: "keeper_activity", relevant: "ALL",
  items: ["Standard","Up-to-stumps","Leg-side-shift","Off-side-shift"],
};
ACTIVITIES_CONFIG["Bounce"] = {
  fieldName: "bounce_category", relevant: "ALL",
  items: ["EXTRA","STANDARD","KEPT_LOW","VARIABLE"],
};
// Spin-only groups
ACTIVITIES_CONFIG["Spin Direction"] = {
  fieldName: "spin_direction", relevant: "SPIN",
  items: ["OFF_SPIN","LEG_SPIN","TOP_SPIN","SLIDER","WRONG_UN","ARM_BALL","CARROM","DOOSRA"],
};
ACTIVITIES_CONFIG["Drift"] = {
  fieldName: "drift_direction", relevant: "SPIN",
  items: ["IN","OUT","NONE"],
};
ACTIVITIES_CONFIG["Turn"] = {
  fieldName: "turn_after_pitch", relevant: "SPIN",
  items: ["BIG","SMALL","STRAIGHT","SKID"],
};

// Right-pane column groupings — only used by the multi-column ProgressDots.
// Reordered 2026-05-16: Keeper + Bounce lead so the operator can flag the
// keeper's stance and the pitch behaviour before committing to the
// delivery type / batsman action chain.
export const ACTIVITY_COLUMNS = [
  ["Keeper"],
  ["Bounce"],
  ["Delivery Type"],
  ["Spin Direction", "Drift", "Turn"],
  ["Stroke", "Foot Movement"],
  ["Shot Connection", "Air Movement", "Control"],
];

// Flat workflow order for the right-pane accordion (one group expanded at
// a time). Keeper / Bounce lead — defaults are auto-applied (Keeper:
// Standard for pace / Up-to-stumps for spin; Bounce: STANDARD), see
// the auto-default effect in BallByBallPage.jsx.
//
// 2026-05-29 — Spin Direction / Drift / Turn moved BELOW Air Movement
// per founder ask. The natural flow is now:
//   Keeper → Bounce → Delivery Type → Stroke → Foot Movement →
//   Shot Connection → Air Movement → Spin Direction → Drift → Turn →
//   Control.
// Stroke now also auto-fills Foot Movement + Shot Connection via the
// STROKE_AUTO_FILL map below (32 stroke types → expected foot + connection).
export const ACTIVITY_FLOW = [
  "Keeper",
  "Bounce",
  "Delivery Type",
  "Stroke",
  "Foot Movement",
  "Shot Connection",
  "Air Movement",
  "Spin Direction",
  "Drift",
  "Turn",
  "Control",
];

// 2026-05-29 — Stroke → (Foot Movement, Shot Connection) auto-fill table
// per founder spec. When the operator picks a stroke (chip click, voice,
// or AI fill) and the foot_movement / shot_connection fields are still
// empty, BBP auto-applies these defaults. Operator overrides still win
// — the auto-fill only sets the field if it's currently blank.
export const STROKE_AUTO_FILL = {
  "Backward Defence": { foot: "Back Foot",      conn: "Normal" },
  "Cover Drive":      { foot: "Front Foot",     conn: "Normal" },
  "Cut":              { foot: "Back Foot",      conn: "Normal" },
  "Dropped":          { foot: "Back Foot",      conn: "Normal" },
  "Fended":           { foot: "Back Foot",      conn: "Normal" },
  "Flick":            { foot: "Front Foot",     conn: "Normal" },
  "Forward Defence":  { foot: "Front Foot",     conn: "Normal" },
  "Glance":           { foot: "Back Foot",      conn: "Normal" },
  "Guided":           { foot: "Back Foot",      conn: "Normal" },
  "Hook":             { foot: "Back Foot",      conn: "Normal" },
  "Late Cut":         { foot: "Back Foot",      conn: "Normal" },
  "Leave":            { foot: "No Movement",    conn: "Left" },
  "No shot":          { foot: "No Movement",    conn: "Left" },
  "Off Drive":        { foot: "Front Foot",     conn: "Normal" },
  "On Drive":         { foot: "Front Foot",     conn: "Normal" },
  "Padded":           { foot: "Front Foot",     conn: "Padded" },
  "Paddle Scoop":     { foot: "Moves Infront",  conn: "Normal" },
  "Paddle Sweep":     { foot: "Front Foot",     conn: "Normal" },
  "Pull":             { foot: "Back Foot",      conn: "Normal" },
  "Pushed":           { foot: "Back Foot",      conn: "Normal" },
  "Reverse Scoop":    { foot: "Moves Infront",  conn: "Normal" },
  "Reverse Sweep":    { foot: "Front Foot",     conn: "Normal" },
  "Scoop":            { foot: "Moves Infront",  conn: "Normal" },
  "Slog":             { foot: "Front Foot",     conn: "Normal" },
  "Slog Sweep":       { foot: "Front Foot",     conn: "Normal" },
  "Square Drive":     { foot: "Back Foot",      conn: "Normal" },
  "Steer":            { foot: "Back Foot",      conn: "Normal" },
  "Straight Drive":   { foot: "Front Foot",     conn: "Normal" },
  "Sweep":            { foot: "Front Foot",     conn: "Normal" },
  "Switch Hit":       { foot: "Front Foot",     conn: "Normal" },
  "Upper Cut":        { foot: "Back Foot",      conn: "Normal" },
  "Worked":           { foot: "Back Foot",      conn: "Normal" },
};
